import { Icon } from "@/react/ui/icon"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "~/react/ui/table"
import { Tag, TagLabel } from "~/react/ui/tag"
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "~/react/ui/tooltip"
import { useEffect, useRef } from "react"
import { cn } from "~/lib/utils"

declare const i18n: {
  t: (key: string, options?: Record<string, unknown>) => string
}

type AbRow = {
  recipients: number // Number of recipients who have already been sent the email
  opens: number
  clicks: number
  replies: number
  successful: number
}

interface AbTestingOverviewTableProps {
  onSampleSizeChange?: (hasSufficientSample: boolean) => void
  selectedVersion?: string | null
}

export function AbTestingOverviewTable({ onSampleSizeChange }: AbTestingOverviewTableProps = {}) {
  // Minimum number of recipients per variant who have already been sent the email
  // required for statistically reliable results
  const MIN_RECIPIENTS_PER_VARIANT = 50

  // TODO(dev, CAMPAIGNS_AB_TESTING): Replace with real A/B testing data once backend/API is ready.
  const rows: AbRow[] = [
    {
      recipients: 59,
      opens: 30,
      clicks: 10,
      replies: 3,
      successful: 41,
    },
    {
      recipients: 59,
      // Deltas (based on counts): opens negative, clicks equal, replies very positive, successful positive below 5%
      opens: 20,
      clicks: 10,
      replies: 9,
      successful: 42,
    },
  ]

  // Calculate total recipients for percentage calculation
  const totalRecipients = rows.reduce((sum, row) => sum + row.recipients, 0)

  // Determine sample health state: sufficient if both variants have at least MIN_RECIPIENTS_PER_VARIANT
  const hasSufficientSample = rows.every((row) => row.recipients >= MIN_RECIPIENTS_PER_VARIANT)
  const sampleHealthState = hasSufficientSample ? "sufficient" : "insufficient"

  // Notify parent component of sample size state
  useEffect(() => {
    if (onSampleSizeChange) {
      onSampleSizeChange(hasSufficientSample)
    }
  }, [hasSufficientSample, onSampleSizeChange])

  const VersionLabel = ({ rowIndex, recipients }: { rowIndex: number; recipients: number }) => {
    const percent = totalRecipients > 0 ? (recipients / totalRecipients) * 100 : 0
    const versionLabel =
      rowIndex === 0 ? i18n.t("js.campaigns.stats.index.version_a") : i18n.t("js.campaigns.stats.index.version_b")
    const isFirstVersion = rowIndex === 0

    return (
      <span className="campaign-stats__ab-version-label">
        <span className="campaign-stats__ab-version-label-name">{versionLabel}</span>
        {/* TODO(dev, CAMPAIGNS_AB_TESTING): Remove this temporary "Winner" tag once backend integration
            is complete and we can determine the winner from actual data. */}
        {isFirstVersion && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="campaign-stats__ab-version-winner-tag">
                  <Tag size="sm" variant="info" className="campaign-stats__ab-version-winner-tag-inner">
                    <TagLabel>{i18n.t("js.campaigns.stats.index.winner")}</TagLabel>
                  </Tag>
                </span>
              </TooltipTrigger>
              <TooltipContent>{i18n.t("js.campaigns.stats.index.winner_tooltip")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <span className="campaign-stats__ab-version-label-details">
          {" · "}
          {i18n.t("js.campaigns.stats.index.recipients_count", { count: recipients })} ({percent.toFixed(0)}%{" "}
          {i18n.t("js.campaigns.stats.index.of_audience")})
        </span>
      </span>
    )
  }

  const versionA = rows[0]

  // Threshold for positive delta to be considered "success" (above 5%)
  const MIN_DELTA_FOR_SUCCESS_PERCENT = 5

  const deltaTag = (baseValue: number, value: number) => {
    // Delta is a % change from base -> value. Example: 3 -> 9 = +200%
    if (baseValue === 0) {
      if (value === 0) {
        return (
          <Tag size="sm" className="campaign-stats__ab-delta-tag">
            <TagLabel>{i18n.t("js.campaigns.stats.index.equal")}</TagLabel>
          </Tag>
        )
      }

      return (
        <Tag size="sm" variant="success" className="campaign-stats__ab-delta-tag">
          <TagLabel>{i18n.t("js.campaigns.stats.index.new")}</TagLabel>
        </Tag>
      )
    }

    const deltaPct = ((value - baseValue) / baseValue) * 100
    const rounded = Number(deltaPct.toFixed(1))

    if (rounded === 0) {
      return (
        <Tag size="sm" className="campaign-stats__ab-delta-tag">
          <TagLabel>{i18n.t("js.campaigns.stats.index.equal")}</TagLabel>
        </Tag>
      )
    }

    if (rounded < 0) {
      return (
        <Tag size="sm" variant="danger" className="campaign-stats__ab-delta-tag">
          <TagLabel>{`${rounded.toFixed(1)}%`}</TagLabel>
        </Tag>
      )
    }

    // 0 < delta <= MIN_DELTA_FOR_SUCCESS_PERCENT => default, delta > MIN_DELTA_FOR_SUCCESS_PERCENT => success
    const variant = rounded > MIN_DELTA_FOR_SUCCESS_PERCENT ? "success" : undefined
    const label = `+${rounded.toFixed(1)}%`
    return (
      <Tag size="sm" variant={variant} className="campaign-stats__ab-delta-tag">
        <TagLabel>{label}</TagLabel>
      </Tag>
    )
  }

  const calculateRate = (count: number, recipients: number) => (recipients > 0 ? (count / recipients) * 100 : 0)

  const MetricValue = ({
    count,
    recipients,
    baseCount,
    showDelta,
  }: {
    count: number
    recipients: number
    baseCount: number
    showDelta: boolean
  }) => (
    <span className="campaign-stats__ab-metric-with-delta">
      <span className="campaign-stats__ab-metric">
        <span className="campaign-stats__ab-metric-value">{count}</span>
        <span className="campaign-stats__ab-metric-rate">({calculateRate(count, recipients).toFixed(1)}%)</span>
      </span>
      {showDelta ? deltaTag(baseCount, count) : null}
    </span>
  )

  interface SampleHealthNoteProps {
    state: "insufficient" | "sufficient"
  }

  // TODO(dev): Make sample health state calculation more sophisticated (e.g., consider statistical
  // significance, confidence intervals, etc.) rather than just a simple threshold check.
  const SampleHealthNote = ({ state }: SampleHealthNoteProps) => {
    const isInsufficient = state === "insufficient"
    const icon = isInsufficient ? "tilde" : "check"
    const label = isInsufficient
      ? i18n.t("js.campaigns.stats.index.small_sample_size")
      : i18n.t("js.campaigns.stats.index.sufficient_sample_size")
    const message = isInsufficient
      ? i18n.t("js.campaigns.stats.index.results_may_change")
      : i18n.t("js.campaigns.stats.index.results_reliable")

    return (
      <div
        className={cn(
          "ab-sample-health-note",
          isInsufficient ? "ab-sample-health-note--insufficient" : "ab-sample-health-note--sufficient",
        )}
      >
        <Icon icon={icon} size="xs" />
        <span className="ab-sample-health-note-label">{label}</span>
        <span> · {message}</span>
      </div>
    )
  }

  // TODO(dev, CAMPAIGNS_AB_TESTING): improve code clarity around the "measure first column width and
  // sync it with the Sequence overview card" behavior (and ideally remove JS syncing once we have a
  // cleaner layout-level solution).
  const firstColumnRef = useRef<HTMLTableCellElement>(null)

  useEffect(() => {
    const updateWidth = () => {
      if (firstColumnRef.current) {
        const width = firstColumnRef.current.offsetWidth
        document.documentElement.style.setProperty(
          "--campaign-stats-ab-table-first-column-width",
          `calc(${width}px - var(--spacing-08))`,
        )
      }
    }

    // Use requestAnimationFrame to ensure the table is rendered
    requestAnimationFrame(updateWidth)
    window.addEventListener("resize", updateWidth)
    return () => window.removeEventListener("resize", updateWidth)
  }, [])

  return (
    <div className="campaign-stats__ab-testing-overview">
      <Table size="sm" className="campaign-stats__ab-testing-table">
        <TableHeader>
          <TableRow>
            <TableHead ref={firstColumnRef} className="campaign-stats__ab-table-col--version">
              {i18n.t("js.campaigns.stats.index.version")}
            </TableHead>
            <TableHead className="campaign-stats__ab-table-head--numeric">
              {i18n.t("js.sending_status.opened")}
            </TableHead>
            <TableHead className="campaign-stats__ab-table-head--numeric">
              {i18n.t("js.sending_status.clicked")}
            </TableHead>
            <TableHead className="campaign-stats__ab-table-head--numeric">
              {i18n.t("js.sending_status.replied")}
            </TableHead>
            <TableHead className="campaign-stats__ab-table-head--numeric">
              {i18n.t("js.sending_status.successful")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell className="campaign-stats__ab-table-col--version">
                <VersionLabel rowIndex={rowIndex} recipients={row.recipients} />
              </TableCell>
              <TableCell className="campaign-stats__ab-table-cell--numeric">
                <MetricValue
                  count={row.opens}
                  recipients={row.recipients}
                  baseCount={versionA.opens}
                  showDelta={rowIndex === 1}
                />
              </TableCell>
              <TableCell className="campaign-stats__ab-table-cell--numeric">
                <MetricValue
                  count={row.clicks}
                  recipients={row.recipients}
                  baseCount={versionA.clicks}
                  showDelta={rowIndex === 1}
                />
              </TableCell>
              <TableCell className="campaign-stats__ab-table-cell--numeric">
                <MetricValue
                  count={row.replies}
                  recipients={row.recipients}
                  baseCount={versionA.replies}
                  showDelta={rowIndex === 1}
                />
              </TableCell>
              <TableCell className="campaign-stats__ab-table-cell--numeric">
                <MetricValue
                  count={row.successful}
                  recipients={row.recipients}
                  baseCount={versionA.successful}
                  showDelta={rowIndex === 1}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <SampleHealthNote state={sampleHealthState} />
    </div>
  )
}
